import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Tooltip,
  Alert,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  CircularProgress,
} from "@mui/material";
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
} from "@mui/icons-material";
import api from "../../services/api";

interface Designation {
  id: number;
  code: string;
  name: string;
  description: string;
  is_active: boolean;
}

interface Grade {
  id: number;
  name: string;
}

const DesignationManagement: React.FC = () => {
  const [designations, setDesignations] = useState<Designation[]>([]);
  const [grades, setGrades] = useState<Grade[]>([]);
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showMappingModal, setShowMappingModal] = useState(false);
  const [selectedDesignation, setSelectedDesignation] =
    useState<Designation | null>(null);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  const [formData, setFormData] = useState({
    code: "",
    name: "",
    description: "",
  });

  useEffect(() => {
    fetchDesignations();
    fetchGrades();
  }, []);

  const fetchDesignations = async () => {
    try {
      const response = await api.get("/designations");
      setDesignations(response.data);
      setLoading(false);
    } catch (error) {
      console.error("Error fetching designations:", error);
      setError("Failed to load designations");
      setLoading(false);
    }
  };

  const fetchGrades = async () => {
    try {
      const response = await api.get("/grades");
      setGrades(response.data);
    } catch (error) {
      console.error("Error fetching grades:", error);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      if (selectedDesignation) {
        await api.put(`/designations/${selectedDesignation.id}`, formData);
        setSuccess("Designation updated successfully");
      } else {
        await api.post("/designations", formData);
        setSuccess("Designation created successfully");
      }
      setShowAddModal(false);
      setFormData({ code: "", name: "", description: "" });
      setSelectedDesignation(null);
      fetchDesignations();
    } catch (error: any) {
      setError(error.response?.data?.error || "Failed to save designation");
    }
  };

  const handleEdit = (designation: Designation) => {
    setSelectedDesignation(designation);
    setFormData({
      code: designation.code,
      name: designation.name,
      description: designation.description || "",
    });
    setShowAddModal(true);
  };

  const handleDelete = async (id: number) => {
    if (
      !window.confirm("Are you sure you want to deactivate this designation?")
    )
      return;
    try {
      await api.delete(`/designations/${id}`);
      setSuccess("Designation deleted successfully");
      fetchDesignations();
    } catch (error: any) {
      setError(error.response?.data?.error || "Failed to delete designation");
    }
  };

  const handleAddGradeMapping = async (gradeId: number) => {
    try {
      await api.post(`/designations/${selectedDesignation?.id}/grades`, {
        gradeId,
      });
      setSuccess("Grade mapping added successfully");
    } catch (error: any) {
      setError(error.response?.data?.error || "Failed to add grade mapping");
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError("")}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess("")}>
          {success}
        </Alert>
      )}

      <Box sx={{ display: "flex", justifyContent: "space-between", mb: 2 }}>
        <Typography variant="h6">Designation Management</Typography>
        <Button
          startIcon={<AddIcon />}
          variant="contained"
          onClick={() => {
            setSelectedDesignation(null);
            setFormData({ code: "", name: "", description: "" });
            setShowAddModal(true);
          }}
        >
          Add New Designation
        </Button>
      </Box>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Code</TableCell>
              <TableCell>Name</TableCell>
              <TableCell>Description</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {designations.map((designation) => (
              <TableRow key={designation.id} hover>
                <TableCell>{designation.code}</TableCell>
                <TableCell>{designation.name}</TableCell>
                <TableCell>{designation.description}</TableCell>
                <TableCell>
                  <Tooltip title="Edit">
                    <IconButton
                      onClick={() => handleEdit(designation)}
                      size="small"
                    >
                      <EditIcon />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Map Grades">
                    <IconButton
                      onClick={() => {
                        setSelectedDesignation(designation);
                        setShowMappingModal(true);
                      }}
                      size="small"
                      color="success"
                    >
                      <AddIcon />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Delete">
                    <IconButton
                      onClick={() => handleDelete(designation.id)}
                      size="small"
                      color="error"
                    >
                      <DeleteIcon />
                    </IconButton>
                  </Tooltip>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Add/Edit Modal */}
      <Dialog
        open={showAddModal}
        onClose={() => setShowAddModal(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {selectedDesignation ? "Edit Designation" : "Add New Designation"}
        </DialogTitle>
        <DialogContent>
          <Box component="form" onSubmit={handleSubmit} sx={{ mt: 2 }}>
            <TextField
              fullWidth
              label="Code"
              value={formData.code}
              onChange={(e) =>
                setFormData({ ...formData, code: e.target.value })
              }
              margin="normal"
              required
            />
            <TextField
              fullWidth
              label="Name"
              value={formData.name}
              onChange={(e) =>
                setFormData({ ...formData, name: e.target.value })
              }
              margin="normal"
              required
            />
            <TextField
              fullWidth
              label="Description"
              value={formData.description}
              onChange={(e) =>
                setFormData({ ...formData, description: e.target.value })
              }
              margin="normal"
              multiline
              rows={3}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setShowAddModal(false);
              setSelectedDesignation(null);
              setFormData({ code: "", name: "", description: "" });
            }}
          >
            Cancel
          </Button>
          <Button onClick={handleSubmit} variant="contained">
            {selectedDesignation ? "Update" : "Create"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Grade Mapping Modal */}
      <Dialog
        open={showMappingModal}
        onClose={() => setShowMappingModal(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Map Grades to {selectedDesignation?.name}</DialogTitle>
        <DialogContent>
          <List sx={{ maxHeight: 400, overflow: "auto" }}>
            {grades.map((grade) => (
              <ListItem key={grade.id}>
                <ListItemText primary={grade.name} />
                <ListItemSecondaryAction>
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={() => handleAddGradeMapping(grade.id)}
                  >
                    Add
                  </Button>
                </ListItemSecondaryAction>
              </ListItem>
            ))}
          </List>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowMappingModal(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default DesignationManagement;
